/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types } from 'mobx-state-tree';
import * as mobx from 'mobx';
import CardModelUtil from 'core/CardModelUtil';

//TODO: move to utility class
// syncExploreVMStateToCards
//
// Track thumbnail model related items & exploreVM related items
// and set properties in card model appropriate to those items
// this is so that ExploreContainerItem view can observe its own Card Model instance
// instead of a shared exploreVM
// Issue with shared exploreVM model is that all views observing it will get rendered
// if displayedCard changes, we don't want to render all explore container items
//
export const syncExploreVMStateToCards = function( self ) {
	const exploreVM = self.exploreVM;
	const thumbnailsModel = exploreVM.thumbnails;

	const findModel = (id) => {
		const items = self.items || [];
		let model = null;
		items.forEach( (item) => {
			if( item.id === id ) {
				model = item;
			}
		} );
		return model;
	};


	//track current thumbnail
	self._mobxDisposeEvents.push(
		mobx.reaction(() => {
			return thumbnailsModel.current.length;
		}, () => {
			if( thumbnailsModel.current &&  thumbnailsModel.current.length > 0 ) {
				thumbnailsModel.current
					.forEach(current => {
						const model = current && findModel(current);
						if(model && model.setCurrentThumbnailInfo) {
							model.setCurrentThumbnailInfo(true, thumbnailsModel );
						}
					});
			}
		})
	);

	//track done thumbnails
	self._mobxDisposeEvents.push(
		mobx.reaction(() => {
			return thumbnailsModel.done.length;
		}, () => {
			let done = undefined;
			if( thumbnailsModel.done &&  thumbnailsModel.done.length > 0 ) {
				done = thumbnailsModel.done;
			}
			done && done.forEach( (item) => {
				const model = findModel(item);
				if(model && model.setCurrentThumbnailInfo && model.setIsDoneThumbnail) {
					model.setCurrentThumbnailInfo(false, thumbnailsModel);
					model.setIsDoneThumbnail(true);
				}
			} );

		})
	);


	const _updateViewStateInCard = (displayedCard, model) => {
		const viewState = {
			canvasTabs : exploreVM.canvasTabs,
			canShowCanvasSidePanel : exploreVM.canShowCanvasSidePanel && exploreVM.canShowCanvasSidePanel(), //TODO:unit test
			thumbnailsModel: thumbnailsModel
		};
		if( model.isDisplayed === true ) {
			viewState.isDisplayed = false;
		} else if(model.id === displayedCard) {
			viewState.isDisplayed = true;
		} else {
			viewState.isDisplayed = false;
		}

		if( model && model.updateViewState) {
			model.updateViewState(viewState);
		}
	};

	const updateViewStateInCard = (displayedCard) => {
		self.items.forEach( (model) => {
			_updateViewStateInCard(displayedCard, model);
		} );
	};

	//update explore VM state for displayed card when exploration is open
	updateViewStateInCard(exploreVM.displayedCard);

	//track displayed card and update corresponding card model with the state
	self._mobxDisposeEvents.push(
		mobx.reaction(() => {
			return self.exploreVM.displayedCard;
		}, (displayedCard) => {
			updateViewStateInCard(displayedCard);
		})
	);

	self._mobxDisposeEvents.push(
		mobx.reaction(() => {
			return self.exploreVM.canShowCanvasSidePanel();
		}, (flag) => {
			const displayedCardModel = findModel(exploreVM.displayedCard);
			if( displayedCardModel && displayedCardModel.updateCanShowCanvasSidePanel) {
				displayedCardModel.updateCanShowCanvasSidePanel(flag);
			}
		})
	);

};


export const ExploreContainerLayoutModelExt = types.model({})
	.named('ExploreContainerLayoutModelExt')
	.props({
		type: types.optional(types.string, 'exploreContainer')
	})
	.volatile(() => ({
		exploreVM: null,
		cardModelUtil: CardModelUtil,
		dashboardApi: null,
		itemCount: 0,
		_mobxDisposeEvents: [],
		api: null
	}))
	.actions((self) => {

		function getCardsFromIds(ids) {
			return ids.map(id => self.findModel(id));
		}

		function getCardIndices(cardIds) {
			const items = self.items;
			return getCardsFromIds(cardIds)
				.map(card => items.indexOf(card));
		}

		function getSuitableInsertBeforeId(ids) {
			const indices = getCardIndices(ids);

			let insertBefore;
			if (indices.length) {
				// Return the id AFTER the current selection.
				const index = Math.max(...indices) + 1;
				const items = self.items;
				if (index < items.length) {
					insertBefore = items[index].id;
				}
			}
			return insertBefore;
		}

		function getNewCardOptions(options) {
			options = options || {};
			// Specify where to insert the new card.
			options.insertBefore = options.insertBefore || getSuitableInsertBeforeId(self.exploreVM.selectedCards.items);
			return options;
		}

		return {
			afterCreate() {
				//when opening exploration, hide all cards except the first one
				if( self.items ) {
					self.items.forEach((item,index) => {
						if(index !== 0) {
							item.setDisplayStyleOff();
						}
					});
				}
			},

			beforeDestroy() {
				// Remove the events.
				self._mobxDisposeEvents.forEach(dispose => dispose());
				if(self.api) {
					self.api.destroy();
					self.api = null;
				}
			},

			setStartingPointsModel(startingPointsModel) {
				self.startingPointsModel = startingPointsModel;
			},

			setCardModelUtil(cardModelUtil) {
				self.cardModelUtil = cardModelUtil;
			},
			addVisCard(widgetSpecs, payload, options) {
				options = getNewCardOptions(options);

				const id = self.cardModelUtil.createCardFromSpec(
					self,
					widgetSpecs,
					{
						payload: payload || 'addVisCard',
						extraLayoutPayload: options
					}
				);
				if (id) {
					self.itemCount++;
					if (options.selectAfterAdd) {
						self.exploreVM.setDisplayedToCard(id);
					} else {
						self.exploreVM.markCardAsNew(id);
					}
				}
				return id;
			},

			//TODO: Is this function called?
			duplicateCard(card, payload, options) {
				options = getNewCardOptions(options);
				if(card.isCompareEnabled) {
					options.model = options.model || {};
					options.model.subType = 'compare';
				}
				const id = self.cardModelUtil.createCardFromExistingCards(
					self,
					[card],
					{
						payload: payload || 'duplicateCard',
						extraLayoutPayload: options
					}
				);
				if (card.isCompareEnabled) {
					self.cardModelUtil.copyPageContextAndRescope(self, {
						layoutId: card.id,
						cloneId: id
					});
				}
				if (id) {
					self.itemCount++;
				}
				return id;
			},

			duplicateCards(cards, opts = {}, payload = 'duplicateCards') {
				const _extraLayoutPayload = getNewCardOptions(opts);

				const options = cards
					.map(card => {
						const extraLayoutPayload = Object.assign({}, _extraLayoutPayload);
						if (card.isCompareEnabled) {
							extraLayoutPayload.model = extraLayoutPayload.model || {};
							extraLayoutPayload.model.subType = 'compare';
						}
						return {
							card,
							options: {
								payload,
								extraLayoutPayload
							}
						};
					});

				const ids = self.cardModelUtil.duplicateCards(self, options);

				if (ids) {
					self.itemCount = self.itemCount + ids.length;
				}

				return ids;
			},

			/**
			* Add a comet card for the selected correlation entry.
			* This function will look for an existing comet card that matches the target and the filters used by the correlation entry
			* If one is found, we switch to it and update the selected driver
			* If none is found, then we create a new comet card using the widget spec supplied by the correlation entry
			* @param {Object} correlationEntry - object representing the correlation entry returned by the badges feature
			*/
			addCometCardForCorrelation(correlationEntry) {
				const widgetSpec = correlationEntry.widgetSpec;
				const widgets = self.dashboardApi.getCanvas().findContent();
				const compareContent = {
					visId:'com.ibm.vis.rave2comet',
					localFilters: widgetSpec.localFilters
				};
				const matchingWidget = widgets.find(widget => {
					const visualization = widget.getFeature('Visualization');
					if (visualization && visualization.matches(compareContent)){
						const dataItems = visualization.getSlots().getSlot('values').getDataItemList();
						if(dataItems && dataItems.length){
							return correlationEntry.target === dataItems[0].getMetadataColumn().getId();
						}
					}
				});

				if(matchingWidget) {
					const model = self.findModel(matchingWidget.getId());
					// the widget id exist.. switch to the parent card
					self.exploreVM.setDisplayedToCard(model.getParent().getId());
					// Update the selections
					if (widgetSpec.customData && widgetSpec.customData.selected) {
						matchingWidget.getFeature('WidgetAPI.deprecated').getVisApi().setCustomDataDecoration('selected', widgetSpec.customData.selected);
					}
				} else {
					self.addVisCard([{ model: widgetSpec }],'exploreSelectCometRecommendation',{ selectAfterAdd:true });
				}
			},
			addCompareCardFromExisting(card1, card2, options) {
				options = getNewCardOptions(options);
				const extraWidgetPayload = {
					properties: [{
						id: 'itemAxis.labels.layoutMode',
						value: 'stagger'
					}, {
						id: 'widget.legend.position',
						value: 'right'
					}]
				};
				options.model = options.model || {};
				options.model.subType = 'compare';
				const id = self.cardModelUtil.createCardFromExistingCards(
					self,
					[card1, card2],
					{
						payload: 'addCompareCardFromExisting',
						extraLayoutPayload: options,
						extraWidgetPayload: extraWidgetPayload,
						widgetIgnoredProps: ['thumbnailId']
					}
				);
				if (id) {
					self.itemCount++;
				}
				return id;
			},

			setExploreVM(exploreVM) {
				self.exploreVM = exploreVM;

				syncExploreVMStateToCards(self);

				self._mobxDisposeEvents.push(
					mobx.reaction(() => {
						return self.items.length;
					}, (len) => {
						if(len === 0) {
							self.exploreVM.setDisplayedToStartingPoints();
						}
					})
				);
			},

			/**
			 * Add cards and datasources by pasting spec from clipboard
			 * @param {object} options
			 * optioins example:
			 * {
			 * 	parentId: 'page0',
			 * 	insertBefore: 'second card id',
			 * 	model: {
			 * 		layout: [], // array of cards
			 * 		widgets: [...], //array of widgets
			 *		dataSources: {}
			* 	}
			* }
			* @param {string} sender
			* @param {object} payloadData
			* @returns {object} - trigger event {eventName, value, preValue, sender, payloadData}
			*/

			addFragment(options, sender, payloadData) {
				if (!Array.isArray(options.model.widgets)) {
					options.model.widgets = Object.values(options.model.widgets);
				}

				if (options.model.layout.items) {
					options.model.layout = Object.values(options.model.layout.items);
				}

				return self.boardModel.addFragment(getNewCardOptions(options), sender, payloadData);
			},

			/**
			 * Delete an array of cards by given IDs
			 *
			 * @param {string[]} ids - Array of card IDs to delete
			 *
			 * @return {boolean} TRUE if the deletion succeeded and FALSE if the deletion failed
			 */
			deleteCards(ids) {
				const result = self.cardModelUtil.deleteCards(self, ids);
				//TODO: Currently we assume all ids are deleted successfully
				if (result && ids && ids.length >0) {
					self.itemCount = self.itemCount - ids.length;
				}
				return result;
			},

			deleteVis(ids) {
				const result = self.cardModelUtil.deleteVisAndSelectRemainingVis(self, ids);
				return result;
			},
			setDashboardApi(api) {
				self.dashboardApi = api;
			},
			getDashboardApi() {
				return self.dashboardApi;
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/core/ExploreContainerLayoutModelExt.js